import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AddCategoryModal from '../components/AddCategoryModal';
import AddProductModal from '../components/AddProductModal';
import './AdminDashboard.css';
import './ProductsPage.css';
import './CategoriesPage.css';

// Importa la configurazione API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/shop/backend/api' 
  : 'http://efwypvu.cluster021.hosting.ovh.net/shop/backend/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const publicUrl = process.env.PUBLIC_URL || '';
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    categorie_totali: 0,
    prodotti_totali: 0,
    ordini_totali: 0
  });
  const [dashboardData, setDashboardData] = useState({
    categorie: [],
    prodotti: [],
    ordini: []
  });
  const [categories, setCategories] = useState([]);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  useEffect(() => {
    checkAuth();
    loadDashboardData();
    loadCategories();
  }, []);

  const checkAuth = () => {
    console.log('🔍 Verifica autenticazione locale...');
    
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    const adminData = localStorage.getItem('admin_data');
    
    if (!isLoggedIn || !adminData) {
      console.log('❌ Non autenticato, reindirizzamento al login...');
      navigate('/amministrazione');
      return;
    }
    
    console.log('✅ Autenticazione verificata!');
    setAdmin(JSON.parse(adminData));
    setLoading(false);
  };

  const loadDashboardData = async () => {
    try {
      console.log('📊 Caricamento dati dashboard...');
      
      // Carica statistiche
      const statsResponse = await fetch(`${API_BASE_URL}/dashboard_stats.php`);
      const statsData = await statsResponse.json();
      
      if (statsData.success) {
        setStats(statsData.data);
        console.log('✅ Statistiche caricate:', statsData.data);
      }

      // Carica dati per tabelle
      const dataResponse = await fetch(`${API_BASE_URL}/dashboard_data.php`);
      const dataData = await dataResponse.json();
      
      if (dataData.success) {
        // Recupera immagine principale per ciascun prodotto (come in ProductsPage)
        const prodottiConImmagini = await Promise.all(
          (dataData.data.prodotti || []).map(async (product) => {
            if (product.foto) {
              try {
                const imgResponse = await fetch(`${API_BASE_URL}/get_images.php?folder=${product.foto}`);
                const imgData = await imgResponse.json();
                if (imgData.success && imgData.data.main_image) {
                  product.mainImageUrl = imgData.data.main_image.full_url;
                }
              } catch (e) {
                console.warn('Errore recupero immagine prodotto dashboard:', e);
              }
            }
            return product;
          })
        );

        // Carica ordini
        const ordiniResponse = await fetch(`${API_BASE_URL}/ordini.php`);
        const ordiniData = await ordiniResponse.json();
        
        setDashboardData({
          categorie: dataData.data.categorie || [],
          prodotti: prodottiConImmagini || [],
          ordini: ordiniData.success ? ordiniData.data.slice(0, 5) : []
        });
        console.log('✅ Dati dashboard caricati:', dataData.data);
      }
      
    } catch (err) {
      console.error('💥 Errore caricamento dashboard:', err);
    }
  };

  const loadCategories = async () => {
    try {
      console.log('📁 Caricamento categorie per modal prodotto...');
      
      const response = await fetch(`${API_BASE_URL}/categorie.php`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Errore durante il caricamento delle categorie');
      }
      
      setCategories(data.data);
      console.log('✅ Categorie caricate:', data.data);
    } catch (err) {
      console.error('💥 Errore caricamento categorie:', err);
    }
  };

  const handleLogout = () => {
    console.log('🚪 Logout...');
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_data');
    navigate('/amministrazione');
  };

  const handleAddCategory = async (categoryData) => {
    console.log('📁 Aggiunta categoria:', categoryData);
    
    try {
      // Semplificato: aggiungi direttamente la categoria
      const response = await fetch(`${API_BASE_URL}/categorie.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(categoryData),
      });

      const data = await response.json();
      console.log('📋 Risposta aggiunta categoria:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'aggiunta della categoria');
      }

      console.log('✅ Categoria aggiunta con successo!');
      // Ricarica i dati dashboard e le categorie
      loadDashboardData();
      loadCategories();
      
    } catch (err) {
      console.error('💥 Errore aggiunta categoria:', err);
      throw err;
    }
  };

  const handleAddProduct = async (productData) => {
    console.log('📦 Aggiunta prodotto:', productData);
    
    try {
      const response = await fetch(`${API_BASE_URL}/prodotti.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      const data = await response.json();
      console.log('📋 Risposta aggiunta prodotto:', data);

      if (!data.success) {
        throw new Error(data.error || 'Errore durante l\'aggiunta del prodotto');
      }

      console.log('✅ Prodotto aggiunto con successo!');
      // Ricarica i dati dashboard
      loadDashboardData();
      
    } catch (err) {
      console.error('💥 Errore aggiunta prodotto:', err);
      throw err;
    }
  };


  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="loading">
          <div className="spinner"></div>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  // Funzioni helper
  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id_categoria === categoryId);
    return category ? category.nome : 'N/A';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusBadge = (pagato) => {
    const isPaid = Number(pagato) === 1;
    return isPaid ? (
      <span className="status-badge paid">Pagato</span>
    ) : (
      <span className="status-badge unpaid">Non Pagato</span>
    );
  };

  return (
    <div className="admin-dashboard admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <img src={`${publicUrl}/logomuseo.png`} alt="Museo Stibbert" className="sidebar-logo" />
        </div>
        
        <div className="sidebar-admin-title">
          <h1><strong style={{color: 'var(--bianco), font-weight: 700, font-size: 1.2rem'}}>Museo Stibbert</strong> Amministrazione</h1>
        </div>

        <nav className="sidebar-nav">
          <button className="nav-item active">
            <i className="fas fa-tachometer-alt"></i>
            <span>Dashboard</span>
          </button>
                  <button className="nav-item" onClick={() => navigate('/amministrazione/categorie')}>
                    <i className="fas fa-tags"></i>
                    <span>Categorie</span>
                  </button>
                  <button className="nav-item" onClick={() => navigate('/amministrazione/prodotti')}>
                    <i className="fas fa-box"></i>
                    <span>Prodotti</span>
                  </button>
          <button className="nav-item" onClick={() => navigate('/amministrazione/ordini')}>
            <i className="fas fa-shopping-cart"></i>
            <span>Ordini</span>
          </button>
                  <button className="nav-item" onClick={() => navigate('/amministrazione/impostazioni')}>
                    <i className="fas fa-cog"></i>
                    <span>Impostazioni</span>
                  </button>
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item logout-item" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="main-header">
          <h1>Dashboard</h1>
          <div className="header-actions">
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddProductModal(true)}
            >
              <i className="fas fa-plus"></i>
              Aggiungi Prodotto
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setShowAddCategoryModal(true)}
            >
              <i className="fas fa-tag"></i>
              Aggiungi Categoria
            </button>
          </div>
        </div>

        <div className="main-content">
          {/* Cards Statistiche */}
          <div className="stats-cards">
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-tags"></i>
              </div>
              <div className="stat-content">
                <h3>{stats.categorie_totali}</h3>
                <p>Categorie Totali</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-box"></i>
              </div>
              <div className="stat-content">
                <h3>{stats.prodotti_totali}</h3>
                <p>Prodotti Totali</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon">
                <i className="fas fa-shopping-cart"></i>
              </div>
              <div className="stat-content">
                <h3>{stats.ordini_totali}</h3>
                <p>Ordini Totali</p>
              </div>
            </div>
          </div>

          {/* Tabelle Anteprima */}
          <div className="dashboard-tables">
            {/* Tabella Ordini - identica alla pagina ordini (senza colonna azioni) */}
            <div className="table-section">
              <div className="table-header">
                <h3>Ultimi Ordini</h3>
                <button 
                  className="btn btn-outline"
                  onClick={() => navigate('/amministrazione/ordini')}
                >
                  Vedi Tutti
                </button>
              </div>
              <div className="ordini-table-container">
                <table className="ordini-table">
                  <thead>
                    <tr>
                      <th>ID Ordine</th>
                      <th>Cliente</th>
                      <th>Totale</th>
                      <th>Stato</th>
                      <th>Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.ordini.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="empty-state">
                          <div className="empty-message">
                            <i className="fas fa-shopping-cart"></i>
                            <p>Nessun ordine trovato</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      dashboardData.ordini.map((ordine) => (
                        <tr key={ordine.id_ordine}>
                          <td className="ordine-id">
                            <strong>#{ordine.id_ordine}</strong>
                          </td>
                          <td className="ordine-cliente">
                            <div className="cliente-info">
                              <strong>{ordine.nome_cliente}</strong>
                            </div>
                          </td>
                          <td className="ordine-totale">
                            <strong>{formatPrice(ordine.totale)}</strong>
                          </td>
                          <td className="ordine-stato">
                            {getStatusBadge(ordine.pagato)}
                          </td>
                          <td className="ordine-data">
                            {formatDate(ordine.data_ordine)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tabella Categorie - identica alla pagina categorie (senza colonna azioni) */}
            <div className="table-section">
              <div className="table-header">
                <h3>Ultime Categorie</h3>
                <button 
                  className="btn btn-outline"
                  onClick={() => navigate('/amministrazione/categorie')}
                >
                  Vedi Tutti
                </button>
              </div>
              <div className="categories-table-container">
                <table className="categories-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Prodotti Associati</th>
                      <th>Data Creazione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.categorie.map((categoria) => (
                      <tr key={categoria.id_categoria}>
                        <td className="category-name">
                          <strong>{categoria.nome}</strong>
                        </td>
                        <td className="products-count">
                          <span className="count-badge">{categoria.numero_prodotti || 0}</span>
                        </td>
                        <td className="category-date">
                          {new Date(categoria.data_creazione).toLocaleDateString('it-IT')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tabella Prodotti - identica alla pagina prodotti (senza colonna azioni) */}
            <div className="table-section">
              <div className="table-header">
                <h3>Ultimi Prodotti</h3>
                <button 
                  className="btn btn-outline"
                  onClick={() => navigate('/amministrazione/prodotti')}
                >
                  Vedi Tutti
                </button>
              </div>
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Prodotto</th>
                      <th>Categoria</th>
                      <th>Prezzo</th>
                      <th>Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.prodotti.map((product) => (
                      <tr key={product.id_prodotto}>
                        <td className="admin-product-info">
                          <div className="product-id">#{product.id_prodotto}</div>
                          <div className="admin-product-image">
                            <img src={product.mainImageUrl || '/placeholder.png'} alt={product.titolo} />
                          </div>
                          <span className="product-title">{product.titolo}</span>
                        </td>
                        <td className="category-cell">
                          <span className="category-badge">{product.categoria_nome || 'N/A'}</span>
                        </td>
                        <td className="price-cell">
                          <span className="price">€{product.prezzo}</span>
                        </td>
                        <td className="status-cell">
                          <span className={`status-badge ${product.attivo ? 'active' : 'inactive'}`}>
                            {product.attivo ? 'Attivo' : 'Inattivo'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modal Aggiungi Categoria */}
      <AddCategoryModal
        isOpen={showAddCategoryModal}
        onClose={() => setShowAddCategoryModal(false)}
        onAddCategory={handleAddCategory}
      />

      {/* Modal Aggiungi Prodotto */}
      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        onAddProduct={handleAddProduct}
        categories={categories}
      />
    </div>
  );
};

export default AdminDashboard;

